import { z } from 'zod';

export const userRoles = ['user', 'admin'] as const;
export type UserRole = (typeof userRoles)[number];

export const userRoleOptions: { value: UserRole; label: string }[] = [
	{ value: 'user', label: 'User' },
	{ value: 'admin', label: 'Admin' },
];

export const UserSchema = z.object({
	id: z.uuid(),
	role: z.enum(userRoles).default(userRoles[0]),
	name: z.string(),
	termsOfServiceVersion: z.number().default(-1),

	// auth fields
	firstName: z.string(),
	lastName: z.string(),
	email: z.email(),
	emailVerified: z.date().nullable(),
	isTwoFactorEnabled: z.boolean(),
	// hasPasskey: z.boolean(),
	// password: z.string().nullable(),
	// image: z.string().nullable().optional(),
	createdAt: z.date(),
	updatedAt: z.date(),
});
export type User = z.infer<typeof UserSchema>;

export const FirstNameSchema = z
	.string()
	.trim()
	.max(63, { message: 'First name must be at most 63 characters long' })
	.min(2, { message: 'First name must be at least 2 characters long' });
export const LastNameSchema = z
	.string()
	.trim()
	.max(63, { message: 'Last name must be at most 63 characters long' })
	.min(2, { message: 'Last name must be at least 2 characters long' });
export const PasswordSchema = z
	.string()
	.min(8, { message: 'Password must be at least 8 characters long' })
	.max(127, { message: 'Password must be at most 127 characters' })
	.regex(/[A-Z]/, { message: 'Password must contain at least 1 uppercase letter' })
	.regex(/[a-z]/, { message: 'Password must contain at least 1 lowercase letter' })
	.regex(/\d/, { message: 'Password must contain at least 1 digit' })
	.regex(/[!@#$%^&*(),.?":{}|<>]/, { message: 'Password must contain at least 1 special character (e.g. @$%^)' });

export const EmailSchema = z.email().max(127).trim();
export const RoleSchema = z.enum(userRoles);

export const UserCreateSchema = z.object({
	firstName: FirstNameSchema,
	lastName: LastNameSchema,
	role: RoleSchema,
	email: EmailSchema,
	// NOTE: setting password is optional when creating a user via console
	// the user themselves will be forced to set a password via the onboarding process
	password: PasswordSchema.optional(),
});
export type UserCreate = z.infer<typeof UserCreateSchema>;

export const UserUpdateSchema = z.object({
	id: z.uuid(),
	role: RoleSchema,
	firstName: FirstNameSchema,
	lastName: LastNameSchema,
});
export type UserUpdate = z.infer<typeof UserUpdateSchema>;
